import { Button } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { File as EntityFile, PurchasePaymentVo } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    GridItem as ZhGridItem
} from "zhihao-ui-react";

import request from "@/api";
import { DATE_PICKER_FORMAT, DATETIME_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import { PAYMENT_MODE, PAYMENT_STATE, PAYMENT_TYPE } from "@/meta/purchase/payment";
import { getIdentityByPayType } from "@/views/pages/purchasingManage/index";
import { dateFormat, dateTimeFormat } from "@/utils";
import { getEnterpriseName } from "@/utils/customer";
import { formatNumber } from "@/utils/number";

export interface PaymentDetailPageProps {
    paymentId?: number | string;
    loadDetail?: (id: number | string) => Promise<PurchasePaymentVo>;
    loadFile?: (id: number | string) => Promise<Partial<EntityFile>>;
}

/** 复用 Vue paymentService.get 的详情接口，保留 `/vo/:id` 路径契约。 */
export async function loadPaymentDetail(id: number | string): Promise<PurchasePaymentVo> {
    const { data } = await request.service.get<PurchasePaymentVo>(`/admin/purchase/payment/vo/${id}`);
    return data;
}

/** 复用 Vue base-item 的 fileId 查询契约，按文件编号获取付款凭证资源。 */
export async function loadPaymentFile(id: number | string): Promise<Partial<EntityFile>> {
    const { data } = await request.service.get<Partial<EntityFile>>(`/dataService/admin/file/${id}`);
    return data;
}

/** 详情值沿用 Vue base-item 的默认占位符。 */
function display(value: unknown): string {
    return value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);
}

/** 凭证文件名沿用 Vue rewrite-file-name，只替换原文件名主干。 */
function getVoucherName(file?: Partial<EntityFile>): string {
    if (!file?.name) return DEFAULT_VALUE;
    const extensionStart = file.name.lastIndexOf(".");
    const extension = extensionStart >= 0 ? file.name.slice(extensionStart) : "";
    return `付款凭证${extension}`;
}

/** 将付款单位和收款单位按 Vue 页面显示为企业名称与身份。 */
function renderEnterpriseWithIdentity(
    enterprise: Parameters<typeof getEnterpriseName>[0],
    paymentType: number | undefined,
    direction: number
) {
    return (
        <span className="inline-flex items-center">
            <span>{getEnterpriseName(enterprise)}</span>
            <span aria-hidden="true" className="mx-2 h-3.5 w-px bg-gray-300" />
            <span>{getIdentityByPayType(paymentType, direction)}</span>
        </span>
    );
}

/** 采购管理-付款详情：迁移 Vue 字段、关联跳转、返回参数和加载失败反馈。 */
export function PaymentDetailPage({
    paymentId,
    loadDetail = loadPaymentDetail,
    loadFile = loadPaymentFile
}: PaymentDetailPageProps) {
    const params = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const currentId = paymentId ?? params.id ?? "";
    const [detail, setDetail] = useState<PurchasePaymentVo>();
    const [voucherFile, setVoucherFile] = useState<Partial<EntityFile>>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!currentId) return;
        let disposed = false;
        void loadDetail(currentId)
            .then(async value => {
                if (disposed) return;
                setDetail(value);
                // Vue base-item 通过 fileId 二次查询文件详情；保留详情响应作为查询失败时的展示回退。
                setVoucherFile(value.file);
                if (value.file?.id) {
                    try {
                        const file = await loadFile(value.file.id);
                        if (!disposed) setVoucherFile(file);
                    } catch {
                        // 文件详情失败时沿用详情接口中的文件信息，不阻断付款详情页面。
                    }
                }
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("付款详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [currentId, loadDetail, loadFile]);

    const payment = detail?.purchasePayment;
    const isInvalid = payment?.state === PAYMENT_STATE.INVALID.code || payment?.state === PAYMENT_STATE.INVALIDING.code;
    const isFullyInvalid = payment?.state === PAYMENT_STATE.INVALID.code;

    /** Vue close 方法固定返回付款列表并触发列表刷新。 */
    const close = () => {
        void navigate("/purchasingManage/payment?refresh=true");
    };

    return (
        <section aria-label="payment-detail-page" className="flex min-h-full flex-col">
            <>
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: display(payment?.id) }]}
                    rightInfos={[
                        { label: "创建时间", value: dateFormat(payment?.createdTime, DATETIME_PICKER_FORMAT) }
                    ]}
                    stateDesc={PAYMENT_STATE.getIntroByCode(payment?.state) || DEFAULT_VALUE}
                    title="付款详情"
                />
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <div className="mb-4 mt-6 text-base font-medium">基本信息</div>
                <ZhGrid cols={4} gap={[30, 20]}>
                    <ZhBaseItem label="所属项目">
                        {payment?.id ? (
                            <Link to={`/purchasingManage/project/info/${payment.projectId}`}>
                                {display(payment.projectName)}
                            </Link>
                        ) : (
                            display(payment?.projectName)
                        )}
                    </ZhBaseItem>
                    <ZhBaseItem label="类型" value={PAYMENT_TYPE.getIntroByCode(payment?.type) || DEFAULT_VALUE} />
                    <ZhBaseItem label="付款单位">
                        {renderEnterpriseWithIdentity(payment?.payCustomerEnterprise, payment?.type, 0)}
                    </ZhBaseItem>
                    <ZhBaseItem label="收款单位">
                        {renderEnterpriseWithIdentity(payment?.collectCustomerEnterprise, payment?.type, 1)}
                    </ZhBaseItem>
                    <ZhBaseItem label="金额" value={`${formatNumber(payment?.amount, 2)}元`} />
                    <ZhBaseItem label="日期" value={dateFormat(payment?.payDate, DATE_PICKER_FORMAT)} />
                    <ZhBaseItem label="方式" value={PAYMENT_MODE.getIntroByCode(payment?.mode) || DEFAULT_VALUE} />
                    <ZhGridItem />
                    <ZhBaseItem label="银行账号" value={display(payment?.bankAccount)} />
                    <ZhBaseItem label="开户名称" value={display(payment?.bankAccountName)} />
                    <ZhBaseItem label="开户行" value={display(payment?.bank)} />
                    <ZhGridItem />
                    <ZhGridItem span={4}>
                        <ZhBaseItem label="凭证文件">
                            {voucherFile?.id ? (
                                <span className="inline-flex items-center gap-4">
                                    <span>{getVoucherName(voucherFile)}</span>
                                    <a href={voucherFile.path} rel="noreferrer" target="_blank">
                                        查看/下载
                                    </a>
                                </span>
                            ) : (
                                DEFAULT_VALUE
                            )}
                        </ZhBaseItem>
                    </ZhGridItem>
                </ZhGrid>

                {isInvalid ? (
                    <>
                        <div className="mb-4 mt-6 text-base font-medium">作废信息</div>
                        <ZhGrid cols={4} gap={[30, 20]}>
                            <ZhBaseItem label="付款单位确认时间" value={dateTimeFormat(payment?.invalidSendTime)} />
                            {isFullyInvalid ? (
                                <ZhBaseItem
                                    label="收款单位确认时间"
                                    value={dateTimeFormat(payment?.invalidConfirmTime)}
                                />
                            ) : null}
                        </ZhGrid>
                    </>
                ) : null}

                <Button className="mt-6" onClick={close}>
                    返回
                </Button>
            </>
        </section>
    );
}

export default PaymentDetailPage;
